import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import type { LinhaDaAuditoria } from "@/api/contrato";
import { useListaDaAuditoria, type FiltrosDaAuditoria } from "@/api/consultas";
import { BlocoDeCodigo } from "@/componentes/BlocoDeCodigo";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { ChipsDeFiltro } from "@/componentes/ChipsDeFiltro";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { PainelLateral } from "@/componentes/PainelLateral";
import { PainelRecolhivel } from "@/componentes/PainelRecolhivel";
import { Picote } from "@/componentes/Picote";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { useFiltrosNaUrl, type DefinicaoDeFiltro } from "@/componentes/useFiltrosNaUrl";
import { dataHora, dia, dinheiro, idCurto } from "@/formato";
import { rotaDoBoleto } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { CarregarMais, contagemDaLista } from "@/telas/boletos/Boletos";
import {
  CampoDeDia,
  Escolha,
  instanteDoDia,
  nomeNaLista,
  type Opcoes,
} from "@/telas/boletos/FiltrosDeBoletos";
import {
  OPCOES_DE_ACAO,
  OPCOES_DE_ENTIDADE,
  rotuloDaAcao,
  rotuloDaEntidade,
  rotuloDoCampo,
} from "./acoes";

/*
 * Auditoria: "quem fez o quê", com o nome que a pessoa tinha no instante da ação.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A TRILHA EXIGE `operador`, E POR ISSO A ABA SOME PARA `leitura`.         │
 * │                                                                          │
 * │ A justificativa é texto livre, e pode citar o sacado (design D9). A rota │
 * │ recusa de qualquer forma, e a consulta nem sai sem o papel.              │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `antes` e `depois` abrem sob demanda, no painel, e como campos: a API já tirou `segredo_ref`
 * e a consulta da URL, e o que sobra é para ler, e não para interpretar JSON.
 */
type ChaveDoFiltro = keyof FiltrosDaAuditoria;

const DEFINICOES: readonly DefinicaoDeFiltro<ChaveDoFiltro>[] = [
  { chave: "operador_id", rotulo: "Operador", textoDoValor: idCurto },
  { chave: "acao", rotulo: "Ação", textoDoValor: rotuloDaAcao },
  { chave: "entidade", rotulo: "Entidade", textoDoValor: rotuloDaEntidade },
  { chave: "entidade_id", rotulo: "Identificador", textoDoValor: idCurto },
  { chave: "ocorrido_de", rotulo: "De", textoDoValor: dia },
  { chave: "ocorrido_ate", rotulo: "Até", textoDoValor: dia },
];

/** Linha antiga pode não ter o nome congelado, e aí sobra o `operador_id`. */
function nomeDoOperador(linha: LinhaDaAuditoria): string {
  return linha.operador_nome || linha.operador_email || idCurto(linha.operador_id);
}

/*
 * A API não lista operadores para esta tela. Eles são dois ou três, e quem agiu há pouco está
 * na primeira página: as opções saem das linhas, e o link com outro operador cai na opção
 * extra de `Escolha`.
 */
function operadoresDa(linhas: readonly LinhaDaAuditoria[]): Opcoes {
  return [...new Map(linhas.map((linha) => [linha.operador_id, nomeDoOperador(linha)]))];
}

export function Auditoria() {
  const { cliente, estado, pode } = useSessao();
  const [aberta, setAberta] = useState<LinhaDaAuditoria | null>(null);

  const filtros = useFiltrosNaUrl(DEFINICOES);
  const lista = useListaDaAuditoria(
    cliente,
    {
      ...filtros.valores,
      ocorrido_de: instanteDoDia(filtros.valores.ocorrido_de, "00:00:00"),
      ocorrido_ate: instanteDoDia(filtros.valores.ocorrido_ate, "23:59:59.999"),
    },
    estado.fase === "dentro" && pode("operador"),
  );

  // O nome vem das linhas, que dependem do filtro: por isso o chip recebe o texto só aqui.
  const operadores = operadoresDa(lista.itens);
  const comNomes = {
    ...filtros,
    aplicados: filtros.aplicados.map((filtro) =>
      filtro.chave === "operador_id" && filtros.valores.operador_id !== undefined
        ? { ...filtro, texto: nomeNaLista(operadores, filtros.valores.operador_id) }
        : filtro,
    ),
  };

  return (
    <div className="flex flex-col gap-3">
      <PainelRecolhivel titulo="Filtros" abertoAoIniciar={false}>
        <div className="flex flex-wrap items-start gap-2">
          <Escolha
            filtros={comNomes}
            chave="operador_id"
            rotulo="Operador"
            todas="Todos"
            opcoes={operadores}
          />
          <Escolha
            filtros={comNomes}
            chave="acao"
            rotulo="Ação"
            todas="Todas"
            opcoes={OPCOES_DE_ACAO}
          />
          <Escolha
            filtros={comNomes}
            chave="entidade"
            rotulo="Entidade"
            todas="Todas"
            opcoes={OPCOES_DE_ENTIDADE}
          />
          <CampoDeDia filtros={comNomes} chave="ocorrido_de" rotulo="De" />
          <CampoDeDia filtros={comNomes} chave="ocorrido_ate" rotulo="Até" />
        </div>
      </PainelRecolhivel>

      <ChipsDeFiltro filtros={comNomes} />

      {lista.itens.length > 0 && lista.erro === null && (
        <span className="rotulo">{contagemDaLista(lista, "registro", "registros")}</span>
      )}

      {lista.erro !== null ? (
        <FaixaDeErro erro={lista.erro} />
      ) : (
        <>
          <Tabela
            rotulo="Auditoria"
            colunas={COLUNAS}
            itens={lista.itens}
            carregando={lista.carregando}
            chaveDe={(linha) => linha.id}
            selecionado={aberta?.id}
            aoAbrir={setAberta}
            vazio={
              filtros.aplicados.length > 0 ? "Nenhum registro com esses filtros." : "Nenhum registro."
            }
          />
          <CarregarMais lista={lista} />
        </>
      )}

      {aberta !== null && <DetalheDaLinha linha={aberta} aoFechar={() => setAberta(null)} />}
    </div>
  );
}

const COLUNAS: readonly Coluna<LinhaDaAuditoria>[] = [
  {
    chave: "ocorrido",
    rotulo: "Ocorrido",
    largura: "132px",
    render: (linha) => (
      <span className="tabular text-texto-2" title={linha.ocorrido_em}>
        {dataHora(linha.ocorrido_em)}
      </span>
    ),
  },
  {
    chave: "operador",
    rotulo: "Operador",
    largura: "minmax(150px, 1fr)",
    render: (linha) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate">{nomeDoOperador(linha)}</span>
        <span className="dados truncate text-[11px] text-texto-3">{linha.operador_email}</span>
      </div>
    ),
  },
  {
    chave: "acao",
    rotulo: "Ação",
    largura: "minmax(170px, 1.2fr)",
    render: (linha) => <span className="truncate">{rotuloDaAcao(linha.acao)}</span>,
  },
  {
    chave: "entidade",
    rotulo: "Entidade",
    largura: "minmax(170px, 1.2fr)",
    render: (linha) => (
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-texto-2">{rotuloDaEntidade(linha.entidade)}</span>
        {linha.entidade_id !== undefined && (
          <span className="dados truncate text-[11px] text-texto-3" title={linha.entidade_id}>
            {idCurto(linha.entidade_id)}
          </span>
        )}
      </div>
    ),
  },
];

const INSTANTE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const DIA = /^\d{4}-\d{2}-\d{2}$/;
const IDENTIFICADOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function semEstado(estado: LinhaDaAuditoria["antes"]): boolean {
  return estado === null || Object.keys(estado).length === 0;
}

function TextoDoEstado({ valor }: { valor: string }) {
  if (INSTANTE.test(valor)) {
    return (
      <span className="tabular" title={valor}>
        {dataHora(valor)}
      </span>
    );
  }
  if (DIA.test(valor)) return <span className="tabular">{dia(valor)}</span>;
  if (IDENTIFICADOR.test(valor)) return <span className="dados break-all">{valor}</span>;
  return <span className="whitespace-pre-wrap">{valor}</span>;
}

/**
 * Cada valor no formato de leitura, para as seis formas do JSON. Dinheiro é inteiro em
 * centavos, e a chave diz qual é.
 */
function ValorDoEstado({ chave, valor }: { chave: string; valor: unknown }) {
  if (valor === null || valor === undefined || valor === "") {
    return <span className="text-texto-3">—</span>;
  }
  if (typeof valor === "boolean") return valor ? "sim" : "não";
  if (typeof valor === "number") {
    return <span className="tabular">{chave.endsWith("centavos") ? dinheiro(valor) : valor}</span>;
  }
  if (typeof valor === "string") return <TextoDoEstado valor={valor} />;

  if (Array.isArray(valor)) {
    if (valor.length === 0) return <span className="text-texto-3">—</span>;
    return (
      <div className="flex flex-col gap-1">
        {valor.map((item, indice) => (
          <ValorDoEstado key={indice} chave={chave} valor={item as unknown} />
        ))}
      </div>
    );
  }
  return <CamposDoEstado estado={valor as Record<string, unknown>} />;
}

function CamposDoEstado({ estado }: { estado: Record<string, unknown> }) {
  return (
    <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1">
      {Object.entries(estado).map(([chave, valor]) => (
        <Fragment key={chave}>
          <dt className="rotulo truncate pt-[2px]" title={chave}>
            {rotuloDoCampo(chave)}
          </dt>
          <dd className="min-w-0 break-words text-corpo text-texto">
            <ValorDoEstado chave={chave} valor={valor} />
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

function EstadoDaLinha({ rotulo, estado }: { rotulo: string; estado: LinhaDaAuditoria["antes"] }) {
  return (
    <section
      aria-label={rotulo}
      className="flex flex-col gap-1 rounded-controle border border-fio bg-superficie-2 px-3 py-2"
    >
      <span aria-hidden="true" className="rotulo">
        {rotulo}
      </span>
      {estado === null || semEstado(estado) ? (
        <span className="text-texto-3">—</span>
      ) : (
        <CamposDoEstado estado={estado} />
      )}
    </section>
  );
}

function DetalheDaLinha({ linha, aoFechar }: { linha: LinhaDaAuditoria; aoFechar: () => void }) {
  const acao = rotuloDaAcao(linha.acao);
  const entidade = rotuloDaEntidade(linha.entidade);

  return (
    <PainelLateral
      aberto
      aoFechar={aoFechar}
      titulo={<span className="text-titulo font-semibold">{acao}</span>}
      descricao={`${acao}, por ${nomeDoOperador(linha)}, em ${dataHora(linha.ocorrido_em)}.`}
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <CaixaDeCampo rotulo="Operador">
            <span className="block truncate">{nomeDoOperador(linha)}</span>
            <span className="dados block truncate text-[11px] text-texto-3">
              {linha.operador_email}
            </span>
          </CaixaDeCampo>
          <CaixaDeCampo rotulo="Ocorrido" dados>
            {dataHora(linha.ocorrido_em)}
          </CaixaDeCampo>
          <CaixaDeCampo rotulo={entidade} dados className="col-span-2">
            {linha.entidade === "boleto" && linha.entidade_id !== undefined ? (
              <Link
                to={rotaDoBoleto(linha.entidade_id)}
                className="text-esferografica underline-offset-2 hover:underline"
              >
                {linha.entidade_id}
              </Link>
            ) : (
              (linha.entidade_id ?? "—")
            )}
          </CaixaDeCampo>
        </div>

        <Picote />

        {semEstado(linha.antes) && semEstado(linha.depois) ? (
          <p className="text-texto-2">Esta ação não grava estado.</p>
        ) : (
          <>
            <EstadoDaLinha rotulo="Antes" estado={linha.antes} />
            <EstadoDaLinha rotulo="Depois" estado={linha.depois} />
          </>
        )}

        {linha.trace_id !== undefined && (
          <BlocoDeCodigo rotulo="trace_id" conteudo={linha.trace_id} />
        )}
      </div>
    </PainelLateral>
  );
}
